import { useCallback, useRef, useState } from "react";

// The right pane shows either the case detail or the (narrower) overview;
// each keeps its own width. Bounds must match .ds-split / .ds-split--overview.
const PANE_BOUNDS = {
  detail: { suffix: "panelWidth", min: 420, max: 900 },
  overview: { suffix: "overviewWidth", min: 280, max: 640 },
};
const TABLE_MIN = 520;

const storageKey = (scope, mode) => `mysolon.ds.${scope}.${PANE_BOUNDS[mode].suffix}`;

const readPaneWidth = (scope, mode) => {
  const { min, max } = PANE_BOUNDS[mode];
  try {
    const value = Number(localStorage.getItem(storageKey(scope, mode)));
    return value >= min && value <= max ? value : null;
  } catch {
    return null;
  }
};

// Resizable table | pane split for one page scope. Widths persist in
// localStorage under mysolon.ds.<scope>.panelWidth / overviewWidth.
export default function useSplitPane(scope, paneMode) {
  const splitRef = useRef(null);
  const [widths, setWidths] = useState(() => ({
    detail: readPaneWidth(scope, "detail"),
    overview: readPaneWidth(scope, "overview"),
  }));

  const startResize = useCallback(
    (e) => {
      e.preventDefault();
      const container = splitRef.current;
      if (!container) return;
      const { min, max: paneMax } = PANE_BOUNDS[paneMode];
      let width = null;
      const onMove = (ev) => {
        const rect = container.getBoundingClientRect();
        const max = Math.min(paneMax, rect.width - TABLE_MIN);
        width = Math.max(min, Math.min(max, rect.right - ev.clientX));
        setWidths((prev) => ({ ...prev, [paneMode]: width }));
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        document.body.classList.remove("ds-resizing");
        try {
          if (width) localStorage.setItem(storageKey(scope, paneMode), String(Math.round(width)));
        } catch {}
      };
      document.body.classList.add("ds-resizing");
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [scope, paneMode]
  );

  const resetWidth = useCallback(() => {
    setWidths((prev) => ({ ...prev, [paneMode]: null }));
    try {
      localStorage.removeItem(storageKey(scope, paneMode));
    } catch {}
  }, [scope, paneMode]);

  const width = widths[paneMode];
  const splitStyle = width ? { "--ds-panel-w": `${width}px` } : undefined;

  return { splitRef, splitStyle, startResize, resetWidth };
}
